import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { BsArrowLeft } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { postForgotPassword } from '../../api/create';

const ForgotPassword: React.FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email('Enter a valid email')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      try {
        const response = await postForgotPassword(values); // Pass the `values` object directly

        if (response && response.success === true) {
          setEmailSent(true);
          setErrorMessage(null);
        } else {
          setEmailSent(false);
          setErrorMessage(response?.message || response?.detail?.message || 'Failed to send email');
        }
      } catch (error) {
        setEmailSent(false);
        setErrorMessage('An error occurred. Please try again later.');
        console.error('Error:', error);
      }
    },
  });

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-[#215564] to-[#163942] bg-white">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg ">
      <h2 className="text-2xl font-bold text-center text-gray-700">
        Forgot Password
      </h2>

      {!emailSent ? (
        <>
          <p className="mt-4 text-center text-gray-500">
            Enter your email address to reset your password
          </p>

          <form onSubmit={formik.handleSubmit} className="space-y-4 mt-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-600"
              >
                Email Address
              </label>
              <input
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formik.touched.email && formik.errors.email
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              />
              {formik.errors.email && formik.touched.email && (
                <div className="mt-1 text-sm text-red-500">
                  {formik.errors.email}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-[#17BE62] rounded-lg hover:bg-[#10a452] focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50"
            >
              {formik.isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          {errorMessage && (
            <p className="mt-4 text-sm text-center text-red-500">
              {errorMessage}
            </p>
          )}

          <div className="text-center mt-4 hover:text-[#10a452]">
            <Link
              to="/"
              className="flex items-center justify-center text-[#17BE62] hover:underline"
            >
              <BsArrowLeft className="mr-2" /> Back to Login
            </Link>
          </div>
        </>
      ) : (
        <div className="space-y-4 text-center">
          <p className="text-lg font-medium text-green-600">
            A password reset link has been sent to your email
          </p>
          <p className="text-sm text-gray-600">
            Didn’t receive the email?{' '}
            <button
              onClick={() => setEmailSent(false)}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Try again
            </button>
            .
          </p>
          <p className="text-sm text-gray-600">
            Entered the wrong email?{' '}
            <button
              onClick={() => setEmailSent(false)}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Change email
            </button>
            .
          </p>
        </div>
      )}
    </div>
    </div>
    </div>
  );
};

export default ForgotPassword;
